<script module lang="ts">
	// Only one badge tooltip is visible at a time across the whole page. Each
	// instance claims a unique id; `activeTip` holds the id of the open one (0 = none).
	let nextTipId = 0;
	let activeTip = $state(0);
</script>

<script lang="ts">
	import { portal } from '$lib/actions';
	import { getBadge } from '$lib/badges';

	// A player's featured-badge emoji shown next to their name, with a tooltip
	// (name + description) on hover or tap. Takes the badge id so it can resolve
	// the label — callers pass the raw `featuredBadge` value. Renders nothing for
	// an unknown/empty id. The tooltip is portaled to <body> so it isn't clipped
	// by row/card overflow.
	let { badgeId = '', size = 'text-sm' }: { badgeId?: string; size?: string } = $props();

	const badge = $derived(getBadge(badgeId));

	const tipId = ++nextTipId;
	let pinned = $state(false);
	let hovered = $state(false);
	let pos = $state({ top: 0, left: 0, below: false });
	const wantOpen = $derived(pinned || hovered);
	// Gate visibility on the shared `activeTip` so only one tooltip shows at a time.
	const open = $derived(activeTip === tipId);

	$effect(() => {
		if (wantOpen) activeTip = tipId;
		else if (activeTip === tipId) activeTip = 0;
	});
	// When another badge's tooltip takes over, drop this one's pin/hover so the
	// tap-toggle stays in sync (no lingering "pinned but hidden" state).
	$effect(() => {
		if (activeTip !== tipId) {
			pinned = false;
			hovered = false;
		}
	});

	function position(el: HTMLElement) {
		const r = el.getBoundingClientRect();
		// Always anchored above (markup applies -translate-y-full); the offset
		// nudges it lower toward the name rather than floating with a gap.
		const left = Math.max(96, Math.min(r.left + r.width / 2, window.innerWidth - 96));
		pos = { top: r.top - 4, left, below: false };
	}
	function onEnter(e: MouseEvent) {
		hovered = true;
		position(e.currentTarget as HTMLElement);
	}
	function onTap(e: MouseEvent) {
		e.stopPropagation();
		e.preventDefault();
		pinned = !pinned;
		if (pinned) position(e.currentTarget as HTMLElement);
	}
</script>

{#if badge}
	<button type="button" data-badge-flair onclick={onTap} onmouseenter={onEnter} onmouseleave={() => (hovered = false)} aria-label="{badge.name} badge" title="" class="shrink-0 cursor-pointer leading-none {size}">
		{#if badge.image}
			<img src={badge.image} alt="" class="inline-block size-[1.4em] align-text-bottom" />
		{:else}
			{badge.icon}
		{/if}
	</button>

	{#if open}
		<div use:portal data-badge-flair-tip role="tooltip" class="fixed z-[9999] w-44 -translate-x-1/2 {pos.below ? '' : '-translate-y-full'}" style:top="{pos.top}px" style:left="{pos.left}px">
			<div class="pixel-frame relative">
				<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
				<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
				<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
				<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
				<div class="relative flex flex-col gap-1 px-3 py-2.5 text-(--nav-action-frame-text)">
					<div class="flex items-center gap-1.5">
						{#if badge.image}
							<img src={badge.image} alt="" class="size-6" />
						{:else}
							<span class="text-lg leading-none">{badge.icon}</span>
						{/if}
						<span class="text-sm font-bold uppercase">{badge.name}</span>
					</div>
					<p class="text-xs leading-snug opacity-80">{badge.description}</p>
				</div>
			</div>
		</div>
	{/if}
{/if}

<svelte:window
	onclick={(e) => {
		if (pinned && !(e.target as HTMLElement)?.closest('[data-badge-flair],[data-badge-flair-tip]')) pinned = false;
	}}
	onkeydown={(e) => {
		if (e.key === 'Escape') pinned = false;
	}}
	onscrollcapture={() => {
		hovered = false;
		pinned = false;
	}}
/>
